import React from 'react'
import styled, { css } from 'styled-components'
import { Text } from '../typography/text'
import { Block } from '../block/block'
import { FileIcon } from '@mote/assets/icons'
import { mote_primary } from '@mote/config/defaults/colors'
import { Alert } from '../alert/alert'
import { Spinner } from '../spinner/spinner'

export const Dropzone = ({
  mimeTypes = [],
  message = 'Drag your files here',
  maxFileSize,
  onFilesAdded,
  loading,
  loadingMessage,
  fileCountLimit
}) => {
  const fileInputRef = React.createRef(null)
  const [errorMessage, setErrorMessage] = React.useState('')
  const [highlight, setHighlight] = React.useState(false)

  function dragOver(e) {
    e.preventDefault()
    setHighlight(true)
  }

  function dragEnter(e) {
    e.preventDefault()
  }

  function dragLeave(e) {
    e.preventDefault()
    setHighlight(false)
  }

  function validateFile(file) {
    if (!mimeTypes.length) {
      console.warn(
        '[DropZone] No mime type list provided, accepting all file types'
      )
      return true
    }
    return mimeTypes.includes(file.type)
  }

  function handleFiles(files) {
    const filesHaveValidMimeType = files.every(file => validateFile(file))
    const individualFileSizeNotOverThreshold = files.every(
      file => file.size <= maxFileSize
    )
    const filesCountAboveThreshold = files.length > fileCountLimit

    if (filesCountAboveThreshold) {
      setErrorMessage(
        `File count limit exceeded. Maximum files you can upload at once is ${fileCountLimit}.`
      )
      return
    }

    if (!individualFileSizeNotOverThreshold) {
      setErrorMessage(
        `File size exceeded. Make sure its below ${humanizeFileSize(
          maxFileSize
        )}`
      )
      return
    }

    if (!filesHaveValidMimeType) {
      setErrorMessage(
        `You can only upload one of the following types: ${mimeTypes
          .map(mimeType => `.${mimeType.split('/')[1]}`)
          .join(',')}`
      )
      return
    }

    onFilesAdded(files)
  }

  function fileDrop(e, eventOrigin) {
    e.preventDefault()

    const files =
      eventOrigin === 'dropEvent' ? e.dataTransfer.files : e.target.files

    if (errorMessage) {
      setErrorMessage(null)
    }

    if (files.length) {
      handleFiles(Array.from(files))
    }
    setHighlight(false)
  }

  function humanizeFileSize(size) {
    if (size === 0) return '0 Bytes'
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB']
    const i = Math.floor(Math.log(size) / Math.log(k))
    return parseFloat((size / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
  }

  function handleDropAreaClick() {
    fileInputRef.current.click()
  }

  return (
    <Root>
      {errorMessage && <Alert message={errorMessage} />}
      <DropArea
        highlight={highlight}
        onDragOver={dragOver}
        onDragEnter={dragEnter}
        onDragLeave={dragLeave}
        onDrop={e => fileDrop(e, 'dropEvent')}
        onClick={handleDropAreaClick}
      >
        {!loading && (
          <Placeholder align='center'>
            <FileIcon />
            <Text tag='p' fontWeight={500}>
              {message}
            </Text>
            <Text tag='small' color='#646F7B'>
              or click to browse{' '}
              {maxFileSize && `(Max ${humanizeFileSize(maxFileSize)})`}
            </Text>

            <input
              style={{ display: 'none' }}
              ref={fileInputRef}
              accept={mimeTypes
                .map(mimeType => `.${mimeType.split('/')[1]}`)
                .join(',')}
              className='FileInput'
              type='file'
              multiple={fileCountLimit > 1}
              onChange={fileDrop}
            />
          </Placeholder>
        )}
        {loading && (
          <LoaderContainer justify='center' align='center' gap={11}>
            <Spinner radius={24} color={mote_primary} />
            <Text tag='p' fontWeight={500}>
              {loadingMessage}
            </Text>
          </LoaderContainer>
        )}
      </DropArea>
    </Root>
  )
}

const Root = styled.div`
  display: flex;
  flex-direction: column;
  height: 175px;
  width: 450px;
`

const DropArea = styled.div`
  display: flex;
  background: #f9f9fb;
  border-radius: 6px;
  height: 100%;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  border: 1px solid #f9f9fb;

  &:hover {
    border-color: ${mote_primary};
  }

  ${({ highlight }) =>
    highlight &&
    css`
      border-color: ${mote_primary};
    `}
`

const Placeholder = styled(Block)``
const LoaderContainer = styled(Block)``
